import { useStytchMember } from "@stytch/react/b2b";
import moment from "moment-timezone";
import { useCreateKnowledgeReport, fetchKnowledgeItemsPage, KnowledgeApi, useGetCommonBlocks, useGetKnowledgeTags, useKnowledgeItemsPaginated, useGetKnowledgeTagsBreakdown, useListOwnersAssignedUsersTasks, ListOwnerAssignments } from "@/lib/api/knowledge";
import { cn, CommonDestinationTimezone, GenerateLoadingOrError } from "@/lib/utils";
import { ArrowLeft, CheckCircle, ChevronDown, ChevronRight, Clock, PenSquare, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import React, { useMemo, useState } from "react";
import { KnowledgeTaskCard } from "./components/KnowledgeTaskCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { Loader } from "@/components/ui/loader";
import { useQueryClient } from "@tanstack/react-query";
import { TagsManagementView } from "./components/TagsManagementView";
import { KnowledgeCommonBlockCard } from "./components/KnowledgeCommonBlockCard";
import { useToast } from "@/components/ui/use-toast";
import { downloadFromS3 } from "@/lib/file";
import { Separator } from "@/components/ui/separator";
import { PermissionType, PossiblePackage } from "lhc_permissions";
import { hasAccessToFeature } from "@/lib/models/auth";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { KnowledgeSearchComponent } from "./components/KnowledgeSearchComponent";

type ExpandedState = Record<string, boolean>;

export function OwnersAssignedOutReport() {
  const navigate = useNavigate();
  const assignedOutListRes = useListOwnersAssignedUsersTasks();
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const { users, usersMap, assignmentsByUser } = useMemo(() => {
    if (!assignedOutListRes.data?.success || !assignedOutListRes.data?.data) {
      return {};
    }

    const assignments = assignedOutListRes.data.data || [];
    const assignmentsByUser: Record<string, ListOwnerAssignments[]> = {};
    const usersMap: Record<string, { name: string }> = {};

    assignments.forEach((assignment: ListOwnerAssignments) => {
      const assignee_id: string = assignment.assignee_id;
      if (!usersMap[assignee_id]) {
        usersMap[assignee_id] = assignment.Assignee;
      }
      if (!assignmentsByUser[assignee_id]) {
        assignmentsByUser[assignee_id] = [];
      }
      assignmentsByUser[assignee_id].push(assignment);
    });

    // Sort profiles within each customer group by commodity and then crop year
    Object.values(assignmentsByUser).forEach(assignments => {
      return assignments.sort((a, b) => {
        if (a.Assignee.name !== b.Assignee.name) {
          return a.Assignee.name.localeCompare(b.Assignee.name);
        }
        return a.KnowledgeItem.title.localeCompare(b.KnowledgeItem.title);
      });
    });

    const users = Object.entries(usersMap).sort((a, b) => {
      return a[1].name.localeCompare(b[1].name);
    }).map(([id, value]) => ({ ...value, id }));

    return { assignmentsByUser, users, usersMap };
  }, [assignedOutListRes.data?.data || []]);

  const loadingOrErrorUi = GenerateLoadingOrError([assignedOutListRes]);
  if (loadingOrErrorUi) return loadingOrErrorUi;


  const toggleExpanded = (id: string) => {
    setExpanded(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };




  return (
    <div className="p-4 space-y-6">
      <div className="flex flex-col lg:flex-row gap-4 lg:justify-between ">
        <div className="p-3 border rounded-md space-y-3">
          <span className="flex flex-row justify-between">
            <h2 className="text-xl font-semibold">Assigned Out Tasks</h2>
          </span>
        </div>
      </div>
      <ScrollArea className="h-[550px]">
        <Table className="risk-compact-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[20px] py-1"></TableHead>
              <TableHead className="py-1">Assignee</TableHead>
              <TableHead className="py-1"></TableHead>
              <TableHead className="py-1"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(users?.length ?? 0) === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-4">
                  No Assigned Out Tasks
                </TableCell>
              </TableRow>
            ) : (
              users!.map((user: { name: string; id: string; }) => (
                <React.Fragment key={user.id}>
                  <TableRow
                    className="cursor-pointer hover:bg-gray-50 customer-row"
                    onClick={() => toggleExpanded(user.id)}
                  >
                    <TableCell>
                      {expanded[user.id] ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </TableCell>
                    <TableCell className="font-medium text-sm">{user.name}</TableCell>
                    <TableCell className="text-sm"></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  {expanded[user.id] && (
                    <TableRow>
                      <TableCell colSpan={5} className="p-0">
                        <div className="bg-gray-50  expanded-row-content">
                          <Table className="risk-compact-table">
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-[200px] py-1 text-xs"></TableHead>
                                <TableHead className="py-1 text-xs">Task</TableHead>
                                <TableHead className="py-1 text-xs">Status</TableHead>
                                <TableHead className="py-1 text-xs"></TableHead>

                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {assignmentsByUser![user.id]!.map((assignment: ListOwnerAssignments) => {
                                return (
                                  <TableRow key={assignment.id} >
                                    <TableCell className="text-xs"></TableCell>
                                    <TableCell className="text-xs" onClick={() => navigate(`/tasks/${assignment.knowledge_item_id}`)}><span className="underline decoration-solid decoration-sky-500">{assignment.KnowledgeItem.title}</span></TableCell>
                                    <TableCell className="text-xs">
                                      <Badge
                                        variant="outline"
                                        className={
                                          assignment.status === 'COMPLETED'
                                            ? "bg-green-50 text-green-700 border-green-200"
                                            : assignment.status === 'IN_PROGRESS'
                                              ? "bg-blue-50 text-blue-700 border-blue-200"
                                              : "bg-amber-50 text-amber-700 border-amber-200"
                                        }
                                      >
                                        {assignment.status === 'COMPLETED' && <CheckCircle className="h-3 w-3 mr-1" />}
                                        {assignment.status === 'IN_PROGRESS' && <PenSquare className="h-3 w-3 mr-1" />}
                                        {assignment.status === 'PENDING' && <Clock className="h-3 w-3 mr-1" />}
                                        {assignment.status.replace('_', ' ')}
                                      </Badge>
                                    </TableCell>

                                    <TableCell>
                                    </TableCell>
                                  </TableRow>
                                )
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </ScrollArea>
    </div >
  )
}
